/**
 * =============================================================================
 * FlowVesselTracker - Advanced Vessel Contour Tracking
 * =============================================================================
 *
 * ALGORITHM:
 * 1. Global Translation: Ring of pixels outside vessel → template matching
 * 2. Local Refinement: Per-vertex adjustment with sub-pixel accuracy (optional)
 * 3. Bidirectional: Forward + backward tracking from reference frame
 *
 * FORMULAS:
 * ---------
 * NCC Score: corr = [Σ(T·I) - (ΣT·ΣI)/n] / √[(Σ(T²)-(ΣT)²/n)·(Σ(I²)-(ΣI)²/n)]
 * SSD Score: Σ(I - T)² / n
 * Penalty:   λ·(dx² + dy²)
 * Total:     score = similarity_score + λ·displacement²
 *
 * Vertex:    score = Σ[I_bilinear(v+δ+r) - T(r)]²
 * Smooth:    v'ᵢ = (vᵢ₋₁ + vᵢ + vᵢ₊₁) / 3
 *
 * USAGE:
 * ------
 * const tracker = new FlowVesselTracker(frames, 512, 512, {bitDepth: 16});
 * const results = tracker.track(polygon, {
 *   searchRadius: 5,
 *   similarityMetric: 'ncc',
 *   displacementPenaltyLambda: 0,  // 0=none, 0.01=gentle, >1=TOO HIGH!
 *   enableShapeAdjustment: true
 * });
 */

export interface Point {
  x: number;
  y: number;
}

export type Polygon = Point[];

export interface FlowVesselTrackerOptions {
  /**
   * Bit depth (8/12/16). For normalization: max = 2^bitDepth - 1
   * @default 16
   */
  bitDepth?: number;

  /**
   * Normalize to [0,1]: value / (2^bitDepth - 1)
   * @default true
   */
  normalize?: boolean;
}

export interface TrackOptions {
  /**
   * Reference frame index. Tracking propagates bidirectionally from here.
   * Tip: Use middle frame for cyclic motion (cardiac imaging).
   * @default 0
   * @range [0, numFrames-1]
   */
  refIndex?: number;

  /**
   * Search radius (pixels). Searches [-r, +r] in x and y.
   * Window size = (2r+1)². Larger = robust to fast motion but slower.
   * @default 5
   * @range 3-15 typical
   */
  searchRadius?: number;

  /**
   * Enable per-vertex refinement after global translation.
   * false = global translation only (faster, rigid tracking)
   * true = global + local vertex adjustment (deformable tracking)
   * @default false
   */
  enableShapeAdjustment?: boolean;

  /**
   * Vertex patch half-size. Patch = (2r+1)×(2r+1).
   * Larger = more context, slower. Smaller = faster, less stable.
   * Only used if enableShapeAdjustment = true.
   * @default 2 (gives 5×5 patch = 25 pixels)
   * @range 1-5 typical
   */
  vertexPatchRadius?: number;

  /**
   * Local search radius per vertex (pixels).
   * Relative to globally-translated position.
   * Only used if enableShapeAdjustment = true.
   * @default 2
   * @range 0.5-5
   */
  vertexSearchRadius?: number;

  /**
   * Sub-pixel step for vertex search. Smaller = finer precision, slower.
   * Positions tested = ((2·radius/step)+1)²
   * Only used if enableShapeAdjustment = true.
   * @default 0.25 (quarter-pixel)
   * @range 0.1-1.0
   * @example
   * 1.0  = integer pixels only (fast)
   * 0.5  = half-pixel precision
   * 0.25 = quarter-pixel (recommended)
   * 0.1  = tenth-pixel (slow, diminishing returns)
   */
  vertexStep?: number;

  /**
   * Apply 3-point smoothing: v'ᵢ = (vᵢ₋₁ + vᵢ + vᵢ₊₁) / 3
   * Only used if enableShapeAdjustment = true.
   * @default true
   */
  smoothShape?: boolean;

  /**
   * Number of smoothing iterations.
   * More = smoother contours, may blur anatomical detail.
   * Only used if smoothShape = true.
   * @default 1
   * @range 1-10 typical
   */
  smoothIterations?: number;

  /**
   * Displacement penalty: score += λ·(dx² + dy²)
   *
   * CRITICAL PARAMETER! Must scale with similarity metric:
   *
   * For NCC (range ~0-2):
   *   λ = 0       No penalty (recommended - trust the tracker)
   *   λ = 0.001   Very gentle temporal smoothing
   *   λ = 0.01    Moderate smoothing (prefers small movements)
   *   λ = 0.1     Strong smoothing (may lag behind motion)
   *   λ = 1-25    TOO HIGH - will freeze contour!
   *
   * For SSD (range depends on intensities):
   *   Scale proportionally to typical SSD values
   *
   * Physics analogy: Spring constant pulling toward previous position.
   *
   * Only applied if enableShapeAdjustment = true.
   * @default 0
   */
  displacementPenaltyLambda?: number;

  /**
   * Similarity metric for global template matching:
   *
   * 'ssd' - Sum of Squared Differences: Σ(I-T)²/n
   *   ✓ Fast computation
   *   ✗ Sensitive to brightness/contrast changes
   *   Good for: Stable intensity images
   *
   * 'ncc' - Normalized Cross-Correlation: [Σ(T·I) - mean] / (σ_T·σ_I)
   *   ✓ Invariant to linear intensity transforms
   *   ✓ Robust to brightness/contrast variations
   *   ✗ Slightly slower
   *   Good for: Medical imaging (recommended)
   *
   * @default 'ncc'
   */
  similarityMetric?: 'ssd' | 'ncc';
}

interface GlobalTemplate {
  coordsX: Int32Array;
  coordsY: Int32Array;
  values: Float32Array;
  sumT: number;
  sumT2: number;
  count: number;
}

interface VertexTemplate {
  relX: Int8Array;
  relY: Int8Array;
  values: Float32Array;
}

interface Offset {
  dx: number;
  dy: number;
  score?: number;
}

export class FlowVesselTracker {
  private width: number;
  private height: number;
  private bitDepth: number;
  private normalize: boolean;
  private frames: Float32Array[];

  /**
   * Surrounding band width (pixels) outside polygon for template extraction.
   * Captures anatomical context without vessel interior intensity.
   */
  private static readonly OUTSIDE_BAND_MARGIN_PX = 6;

  /**
   * Create tracker instance.
   * @param frames - Array of image frames in row-major order: index = y·width + x
   * @param width - Image width in pixels
   * @param height - Image height in pixels
   * @param options - Configuration (bitDepth, normalize)
   */
  constructor(
    frames: Array<ArrayLike<number>>,
    width: number,
    height: number,
    options: FlowVesselTrackerOptions = {},
  ) {
    this.width = width;
    this.height = height;
    this.bitDepth = options.bitDepth ?? 16;
    this.normalize = options.normalize !== false;
    this.frames = frames.map(f => this.toFloatFrame(f));
  }

  /**
   * Track polygon across all frames.
   *
   * ALGORITHM:
   * 1. Build global template (ring around polygon) from reference frame
   * 2. Build vertex templates if shape adjustment enabled
   * 3. Track forward: find best offset each frame, apply to polygon
   * 4. Track backward: same process in reverse
   * 5. Optional: refine vertices and smooth
   *
   * @param polygon - Initial polygon at reference frame
   * @param options - Tracking parameters
   * @returns Array of tracked polygons, one per frame
   */
  public track(polygon: Polygon, options: TrackOptions = {}): Polygon[] {
    const numFrames = this.frames.length;
    if (numFrames === 0) return [];

    const refIndex = options.refIndex ?? 0;
    const searchRadius = options.searchRadius ?? 5;
    const enableShapeAdjustment = options.enableShapeAdjustment === true;
    const vertexPatchRadius = options.vertexPatchRadius ?? 2;
    const vertexSearchRadius = options.vertexSearchRadius ?? 2;
    const vertexStep = options.vertexStep ?? 0.25;
    const smoothShape = options.smoothShape !== false;
    const smoothIterations = options.smoothIterations ?? 1;
    const similarity = options.similarityMetric ?? 'ncc';

    const effectiveLambda = enableShapeAdjustment
      ? (options.displacementPenaltyLambda ?? 0)
      : 0;

    if (refIndex < 0 || refIndex >= numFrames) {
      throw new Error('refIndex out of range');
    }

    const globalTemplate = this.buildGlobalTemplate(
      this.frames[refIndex],
      polygon,
    );

    let vertexTemplates: (VertexTemplate | null)[] | null = null;
    if (enableShapeAdjustment) {
      vertexTemplates = this.buildVertexTemplates(
        this.frames[refIndex],
        polygon,
        vertexPatchRadius,
      );
    }

    const resultPolygons: Polygon[] = Array.from({ length: numFrames });
    resultPolygons[refIndex] = polygon.map(p => ({ x: p.x, y: p.y }));

    // Track forward from reference
    let currentOffsetF: Offset = { dx: 0, dy: 0 };
    for (let t = refIndex + 1; t < numFrames; t++) {
      const frame = this.frames[t];
      const bestOffset = this.findBestGlobalOffset(
        frame,
        globalTemplate,
        currentOffsetF,
        searchRadius,
        effectiveLambda,
        similarity,
      );
      currentOffsetF = bestOffset;
      let poly = FlowVesselTracker.applyOffsetToPolygon(
        polygon,
        currentOffsetF,
      );
      if (enableShapeAdjustment && vertexTemplates) {
        poly = this.refinePolygonShape(
          frame,
          poly,
          polygon,
          vertexTemplates,
          vertexSearchRadius,
          vertexStep,
          smoothShape,
          smoothIterations,
        );
      }
      resultPolygons[t] = poly;
    }

    // Track backward from reference
    let currentOffsetB: Offset = { dx: 0, dy: 0 };
    for (let t = refIndex - 1; t >= 0; t--) {
      const frame = this.frames[t];
      const bestOffset = this.findBestGlobalOffset(
        frame,
        globalTemplate,
        currentOffsetB,
        searchRadius,
        effectiveLambda,
        similarity,
      );
      currentOffsetB = bestOffset;
      let poly = FlowVesselTracker.applyOffsetToPolygon(
        polygon,
        currentOffsetB,
      );
      if (enableShapeAdjustment && vertexTemplates) {
        poly = this.refinePolygonShape(
          frame,
          poly,
          polygon,
          vertexTemplates,
          vertexSearchRadius,
          vertexStep,
          smoothShape,
          smoothIterations,
        );
      }
      resultPolygons[t] = poly;
    }

    return resultPolygons;
  }

  /**
   * Convert frame to normalized Float32Array.
   * Formula: normalized = value / (2^bitDepth - 1)
   */
  private toFloatFrame(frame: ArrayLike<number>): Float32Array {
    const len = frame.length;
    const out = new Float32Array(len);
    let maxVal = 1.0;
    if (this.normalize) maxVal = (1 << this.bitDepth) - 1;
    for (let i = 0; i < len; i++) {
      out[i] = this.normalize ? frame[i] / maxVal : Number(frame[i]);
    }
    return out;
  }

  /**
   * Apply translation to polygon: p' = p + (dx, dy)
   */
  private static applyOffsetToPolygon(
    polygon: Polygon,
    offset: Offset,
  ): Polygon {
    const { dx, dy } = offset;
    return polygon.map(p => ({ x: p.x + dx, y: p.y + dy }));
  }

  /**
   * Build global template from ring of pixels surrounding polygon.
   *
   * STRATEGY:
   * - Uses pixels OUTSIDE vessel (insensitive to vessel intensity)
   * - Band width = OUTSIDE_BAND_MARGIN_PX (6 pixels)
   * - Captures surrounding anatomical context
   * - Typical size: 50-500 pixels
   *
   * @param refFrame - Reference frame data
   * @param polygon - Vessel boundary
   * @returns Template with coordinates and NCC statistics
   */
  private buildGlobalTemplate(
    refFrame: Float32Array,
    polygon: Polygon,
  ): GlobalTemplate {
    const W = this.width;
    const H = this.height;

    // Compute polygon bounding box
    const bb = polygon.reduce(
      (acc, p) => ({
        minX: Math.min(acc.minX, p.x),
        maxX: Math.max(acc.maxX, p.x),
        minY: Math.min(acc.minY, p.y),
        maxY: Math.max(acc.maxY, p.y),
      }),
      { minX: Infinity, maxX: -Infinity, minY: Infinity, maxY: -Infinity },
    );
    const { minX, maxX, minY, maxY } = bb;

    // Expand bbox for search region
    const m = FlowVesselTracker.OUTSIDE_BAND_MARGIN_PX;
    const bbMinX = Math.max(0, Math.floor(minX) - m);
    const bbMinY = Math.max(0, Math.floor(minY) - m);
    const bbMaxX = Math.min(W - 1, Math.ceil(maxX) + m);
    const bbMaxY = Math.min(H - 1, Math.ceil(maxY) + m);

    const bandWidth = m;
    const coordsX: number[] = [];
    const coordsY: number[] = [];
    const values: number[] = [];

    // Extract pixels in surrounding band
    for (let y = bbMinY; y <= bbMaxY; y++) {
      for (let x = bbMinX; x <= bbMaxX; x++) {
        const cx = x + 0.5;
        const cy = y + 0.5;
        if (FlowVesselTracker.pointInPolygon(cx, cy, polygon)) continue;
        const d = FlowVesselTracker.distancePointToPolygonEdges(
          cx,
          cy,
          polygon,
        );
        if (d > bandWidth) continue;
        coordsX.push(x);
        coordsY.push(y);
        values.push(refFrame[y * W + x]);
      }
    }

    if (coordsX.length === 0) {
      throw new Error('Template has no pixels. Polygon too close to edge?');
    }

    // Convert to typed arrays and compute NCC statistics
    const coordsXArr = new Int32Array(coordsX);
    const coordsYArr = new Int32Array(coordsY);
    const valuesArr = new Float32Array(values);

    let sumT = 0;
    let sumT2 = 0;
    for (let i = 0; i < coordsX.length; i++) {
      const v = values[i];
      sumT += v;
      sumT2 += v * v;
    }

    return {
      coordsX: coordsXArr,
      coordsY: coordsYArr,
      values: valuesArr,
      sumT,
      sumT2,
      count: coordsX.length,
    };
  }

  /**
   * Distance from point to nearest polygon edge.
   */
  private static distancePointToPolygonEdges(
    x: number,
    y: number,
    polygon: Polygon,
  ): number {
    let best = Infinity;
    const n = polygon.length;
    for (let i = 0; i < n; i++) {
      const a = polygon[i];
      const b = polygon[(i + 1) % n];
      const d = FlowVesselTracker.distancePointToSegment(
        x,
        y,
        a.x,
        a.y,
        b.x,
        b.y,
      );
      if (d < best) best = d;
    }
    return best;
  }

  /**
   * Distance from point to line segment.
   * Projects point onto line, clamps to segment endpoints.
   * Formula: t = (AP·AB)/|AB|², C = A + t·AB, distance = |PC|
   */
  private static distancePointToSegment(
    px: number,
    py: number,
    ax: number,
    ay: number,
    bx: number,
    by: number,
  ): number {
    const abx = bx - ax;
    const aby = by - ay;
    const apx = px - ax;
    const apy = py - ay;
    const ab2 = abx * abx + aby * aby;
    if (ab2 <= 1e-12) {
      const dx = px - ax;
      const dy = py - ay;
      return Math.sqrt(dx * dx + dy * dy);
    }
    let t = (apx * abx + apy * aby) / ab2;
    if (t < 0) t = 0;
    else if (t > 1) t = 1;
    const cx = ax + t * abx;
    const cy = ay + t * aby;
    const dx = px - cx;
    const dy = py - cy;
    return Math.sqrt(dx * dx + dy * dy);
  }

  /**
   * Find best global translation offset via template matching.
   *
   * FEATURES:
   * - Partial template matching: handles image boundaries
   * - Requires ≥30% of template pixels valid
   * - Supports NCC and SSD metrics
   * - Optional displacement penalty: λ·(dx² + dy²)
   *
   * ALGORITHM:
   * For each offset (dx,dy) in search window:
   *   1. Count valid pixels (within image bounds)
   *   2. Compute similarity score using valid pixels
   *   3. Add displacement penalty if λ > 0
   *   4. Track minimum score
   *
   * @param frame - Current frame to search
   * @param template - Global template from reference
   * @param centerOffset - Previous offset (for penalty)
   * @param searchRadius - Search window half-size
   * @param lambda - Displacement penalty weight
   * @param similarityMetric - 'ncc' or 'ssd'
   * @returns Best offset with score
   */
  private findBestGlobalOffset(
    frame: Float32Array,
    template: GlobalTemplate,
    centerOffset: Offset,
    searchRadius: number,
    lambda: number,
    similarityMetric: 'ssd' | 'ncc',
  ): Offset {
    const W = this.width;
    const H = this.height;
    const { coordsX, coordsY, values, count } = template;

    let bestScore = Infinity;
    let bestDx = centerOffset.dx;
    let bestDy = centerOffset.dy;
    const baseDx = centerOffset.dx;
    const baseDy = centerOffset.dy;

    // Require at least 30% of template valid
    const minValidPixels = Math.max(5, Math.floor(count * 0.3));

    for (let dy = -searchRadius; dy <= searchRadius; dy++) {
      for (let dx = -searchRadius; dx <= searchRadius; dx++) {
        let score: number;

        if (similarityMetric === 'ncc') {
          // NCC: corr = [Σ(T·I) - (ΣT·ΣI)/n] / √(σ_T²·σ_I²)
          let sumI = 0;
          let sumI2 = 0;
          let sumTI = 0;
          let sumTValid = 0;
          let sumT2Valid = 0;
          let validCount = 0;

          for (let i = 0; i < count; i++) {
            const sx = coordsX[i] + dx;
            const sy = coordsY[i] + dy;
            if (sx < 0 || sx >= W || sy < 0 || sy >= H) continue;
            const I = frame[sy * W + sx];
            const T = values[i];
            sumI += I;
            sumI2 += I * I;
            sumTI += T * I;
            sumTValid += T;
            sumT2Valid += T * T;
            validCount++;
          }

          if (validCount < minValidPixels) continue;

          const n = validCount;
          const denomT = sumT2Valid - (sumTValid * sumTValid) / n;
          const denomI = sumI2 - (sumI * sumI) / n;
          if (denomT <= 1e-12 || denomI <= 1e-12) continue;

          const numerator = sumTI - (sumI * sumTValid) / n;
          const denom = Math.sqrt(denomT * denomI);
          if (denom <= 1e-12) continue;

          const corr = numerator / denom;
          score = -corr; // Negate for minimization
        }
        else {
          // SSD: Σ(I-T)² / n
          let sum = 0;
          let validCount = 0;
          for (let i = 0; i < count; i++) {
            const sx = coordsX[i] + dx;
            const sy = coordsY[i] + dy;
            if (sx < 0 || sx >= W || sy < 0 || sy >= H) continue;
            const diff = frame[sy * W + sx] - values[i];
            sum += diff * diff;
            validCount++;
          }
          if (validCount < minValidPixels) continue;
          score = sum / validCount;
        }

        // Add displacement penalty: λ·(dx² + dy²)
        if (lambda > 0) {
          const dispX = dx - baseDx;
          const dispY = dy - baseDy;
          score += lambda * (dispX * dispX + dispY * dispY);
        }

        if (score < bestScore) {
          bestScore = score;
          bestDx = dx;
          bestDy = dy;
        }
      }
    }

    return { dx: bestDx, dy: bestDy, score: bestScore };
  }

  /**
   * Build per-vertex templates for shape refinement.
   * Extracts (2r+1)×(2r+1) patch around each vertex.
   */
  private buildVertexTemplates(
    refFrame: Float32Array,
    polygon: Polygon,
    patchRadius: number,
  ): (VertexTemplate | null)[] {
    const W = this.width;
    const H = this.height;
    const templates: (VertexTemplate | null)[] = Array.from({
      length: polygon.length,
    });

    for (let k = 0; k < polygon.length; k++) {
      const v = polygon[k];
      const cx = Math.round(v.x);
      const cy = Math.round(v.y);
      const relX: number[] = [];
      const relY: number[] = [];
      const values: number[] = [];

      for (let dy = -patchRadius; dy <= patchRadius; dy++) {
        for (let dx = -patchRadius; dx <= patchRadius; dx++) {
          const sx = cx + dx;
          const sy = cy + dy;
          if (sx < 0 || sx >= W || sy < 0 || sy >= H) continue;
          relX.push(dx);
          relY.push(dy);
          values.push(refFrame[sy * W + sx]);
        }
      }
      templates[k]
        = values.length === 0
          ? null
          : {
              relX: new Int8Array(relX),
              relY: new Int8Array(relY),
              values: new Float32Array(values),
            };
    }
    return templates;
  }

  /**
   * Refine polygon by independently adjusting each vertex.
   * Uses SSD with bilinear interpolation for sub-pixel accuracy.
   */
  private refinePolygonShape(
    frame: Float32Array,
    basePolygon: Polygon,
    _refPolygon: Polygon,
    vertexTemplates: (VertexTemplate | null)[],
    searchRadius: number,
    vertexStep: number,
    smoothShape: boolean,
    smoothIterations: number,
  ): Polygon {
    const refined: Polygon = Array.from({ length: basePolygon.length });
    for (let k = 0; k < basePolygon.length; k++) {
      const tpl = vertexTemplates[k];
      if (!tpl) {
        refined[k] = { x: basePolygon[k].x, y: basePolygon[k].y };
        continue;
      }
      const baseV = basePolygon[k];
      refined[k] = this.findBestVertexPosition(
        frame,
        baseV.x,
        baseV.y,
        tpl,
        searchRadius,
        vertexStep,
      );
    }
    return smoothShape
      ? FlowVesselTracker.smoothPolygon(refined, smoothIterations)
      : refined;
  }

  /**
   * Bilinear interpolation: I(x,y) = weighted average of 4 corners.
   * Formula: I₀ = I₀₀(1-dx) + I₁₀·dx,  I₁ = I₀₁(1-dx) + I₁₁·dx
   *          I = I₀(1-dy) + I₁·dy
   */
  private sampleBilinear(
    frame: Float32Array,
    x: number,
    y: number,
  ): number | null {
    const W = this.width;
    const H = this.height;
    if (x < 0 || x >= W - 1 || y < 0 || y >= H - 1) return null;
    const x0 = Math.floor(x);
    const y0 = Math.floor(y);
    const dx = x - x0;
    const dy = y - y0;
    const I00 = frame[y0 * W + x0];
    const I10 = frame[y0 * W + x0 + 1];
    const I01 = frame[(y0 + 1) * W + x0];
    const I11 = frame[(y0 + 1) * W + x0 + 1];
    const I0 = I00 * (1 - dx) + I10 * dx;
    const I1 = I01 * (1 - dx) + I11 * dx;
    return I0 * (1 - dy) + I1 * dy;
  }

  /**
   * Find best vertex position using SSD with sub-pixel search.
   * Score = Σ[I_bilinear(v+δ+r) - T(r)]²
   */
  private findBestVertexPosition(
    frame: Float32Array,
    baseX: number,
    baseY: number,
    template: VertexTemplate,
    searchRadius: number,
    step: number,
  ): Point {
    const { relX, relY, values } = template;
    let bestScore = Infinity;
    let bestX = baseX;
    let bestY = baseY;

    for (let dy = -searchRadius; dy <= searchRadius; dy += step) {
      for (let dx = -searchRadius; dx <= searchRadius; dx += step) {
        let sum = 0;
        let valid = true;
        for (let i = 0; i < relX.length; i++) {
          const I = this.sampleBilinear(
            frame,
            baseX + dx + relX[i],
            baseY + dy + relY[i],
          );
          if (I === null) {
            valid = false;
            break;
          }
          sum += (I - values[i]) ** 2;
        }
        if (valid && sum < bestScore) {
          bestScore = sum;
          bestX = baseX + dx;
          bestY = baseY + dy;
        }
      }
    }
    return { x: bestX, y: bestY };
  }

  /**
   * Smooth polygon: v'ᵢ = (vᵢ₋₁ + vᵢ + vᵢ₊₁) / 3
   * Applied iteratively N times (Laplacian smoothing).
   */
  private static smoothPolygon(polygon: Polygon, iterations: number): Polygon {
    const n = polygon.length;
    if (n <= 2 || iterations <= 0)
      return polygon.map(p => ({ x: p.x, y: p.y }));
    let current = polygon.map(p => ({ x: p.x, y: p.y }));
    for (let it = 0; it < iterations; it++) {
      const next: Polygon = Array.from({ length: n });
      for (let i = 0; i < n; i++) {
        const prev = current[(i - 1 + n) % n];
        const curr = current[i];
        const nextP = current[(i + 1) % n];
        next[i] = {
          x: (prev.x + curr.x + nextP.x) / 3,
          y: (prev.y + curr.y + nextP.y) / 3,
        };
      }
      current = next;
    }
    return current;
  }

  /**
   * Point-in-polygon: ray casting algorithm.
   * Casts horizontal ray, counts edge intersections. Odd = inside.
   */
  private static pointInPolygon(
    x: number,
    y: number,
    polygon: Polygon,
  ): boolean {
    let inside = false;
    const n = polygon.length;
    for (let i = 0, j = n - 1; i < n; j = i++) {
      const xi = polygon[i].x;
      const yi = polygon[i].y;
      const xj = polygon[j].x;
      const yj = polygon[j].y;
      const intersect
        = (yi > y) !== (yj > y)
          && x < ((xj - xi) * (y - yi)) / (yj - yi + 1e-12) + xi;
      if (intersect) inside = !inside;
    }
    return inside;
  }
}

// --- helpers ---

function sampleBilinear(
  frame: ArrayLike<number>,
  width: number,
  x: number,
  y: number,
): number {
  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const dx = x - x0;
  const dy = y - y0;
  const I00 = frame[y0 * width + x0];
  const I10 = frame[y0 * width + x0 + 1];
  const I01 = frame[(y0 + 1) * width + x0];
  const I11 = frame[(y0 + 1) * width + x0 + 1];
  return (
    (I00 * (1 - dx) + I10 * dx) * (1 - dy) + (I01 * (1 - dx) + I11 * dx) * dy
  );
}

function samplePatchMean(
  frame: ArrayLike<number>,
  width: number,
  height: number,
  cx: number,
  cy: number,
  radius: number,
): number {
  let sum = 0;
  let count = 0;
  const x0 = Math.max(0, Math.floor(cx) - radius);
  const y0 = Math.max(0, Math.floor(cy) - radius);
  const x1 = Math.min(width - 1, Math.floor(cx) + radius);
  const y1 = Math.min(height - 1, Math.floor(cy) + radius);
  for (let y = y0; y <= y1; y++) {
    for (let x = x0; x <= x1; x++) {
      sum += frame[y * width + x];
      count++;
    }
  }
  return count > 0 ? sum / count : 0;
}

function smoothPolygonClosed(polygon: Polygon, iterations: number): Polygon {
  const n = polygon.length;
  if (n <= 2) return polygon;
  let current = polygon.map(p => ({ x: p.x, y: p.y }));
  for (let it = 0; it < iterations; it++) {
    const next: Polygon = Array.from({ length: n });
    for (let i = 0; i < n; i++) {
      const prev = current[(i - 1 + n) % n];
      const curr = current[i];
      const nextP = current[(i + 1) % n];
      next[i] = {
        x: (prev.x + curr.x + nextP.x) / 3,
        y: (prev.y + curr.y + nextP.y) / 3,
      };
    }
    current = next;
  }
  return current;
}

// Segment vessel lumen from seed point
export interface SeedSegmentOptions {
  /** Number of radial rays. More = smoother boundary. @default 36 */
  numRays?: number;

  /** Maximum search radius along each ray (pixels). @default 30 */
  maxRadius?: number;

  /** Minimum radius to start searching (skip center noise). @default 2 */
  minRadius?: number;

  /** Step size along ray (pixels). Smaller = finer edge detection. @default 0.5 */
  rayStep?: number;

  /**
   * Edge detection: gradient threshold as fraction of seed intensity.
   *  A point is "edge" when intensity drops below seed * threshold.
   *  @default 0.5
   */
  intensityDropThreshold?: number;

  /**
   * Alternative: use maximum gradient magnitude instead of threshold.
   *  true = find steepest drop (robust), false = use threshold (fast).
   *  @default true
   */
  useGradientEdge?: boolean;

  /** Smoothing iterations on the resulting polygon. @default 2 */
  smoothIterations?: number;
}

export function segmentVesselFromSeed(
  frame: ArrayLike<number>,
  width: number,
  height: number,
  seedX: number,
  seedY: number,
  options: SeedSegmentOptions = {},
): Polygon {
  const numRays = options.numRays ?? 36;
  const maxRadius = options.maxRadius ?? 30;
  const minRadius = options.minRadius ?? 2;
  const rayStep = options.rayStep ?? 0.5;
  const threshold = options.intensityDropThreshold ?? 0.5;
  const useGradient = options.useGradientEdge !== false;
  const smoothIter = options.smoothIterations ?? 2;

  // Sample seed intensity (average 3x3 patch for robustness)
  const seedIntensity = samplePatchMean(frame, width, height, seedX, seedY, 3);
  if (seedIntensity <= 0) return [];

  const boundaryPoints: Point[] = [];
  const angleStep = (2 * Math.PI) / numRays;

  for (let i = 0; i < numRays; i++) {
    const angle = i * angleStep;
    const dirX = Math.cos(angle);
    const dirY = Math.sin(angle);

    let edgeR = maxRadius;

    if (useGradient) {
      const cutoff = seedIntensity * threshold;
      for (let r = minRadius; r <= maxRadius; r += rayStep) {
        const sx = seedX + dirX * r;
        const sy = seedY + dirY * r;
        if (sx < 0 || sx >= width - 1 || sy < 0 || sy >= height - 1) {
          edgeR = r;
          break;
        }
        const val = sampleBilinear(frame, width, sx, sy);
        if (val < cutoff) {
          edgeR = r;
          break; // stop at first crossing
        }
      }
    }
    else {
      // Threshold: find where intensity drops below fraction of seed
      const cutoff = seedIntensity * threshold;
      for (let r = minRadius; r <= maxRadius; r += rayStep) {
        const sx = seedX + dirX * r;
        const sy = seedY + dirY * r;
        if (sx < 0 || sx >= width - 1 || sy < 0 || sy >= height - 1) {
          edgeR = r;
          break;
        }
        if (sampleBilinear(frame, width, sx, sy) < cutoff) {
          edgeR = r;
          break;
        }
      }
    }

    boundaryPoints.push({
      x: seedX + dirX * edgeR,
      y: seedY + dirY * edgeR,
    });
  }

  // Smooth the polygon
  return smoothPolygonClosed(boundaryPoints, smoothIter);
}
